import React, { useEffect, useRef, useState } from 'react'
import { Modal } from 'react-bootstrap'
import { createPortal } from 'react-dom'
import { Field, useFormik } from 'formik';
import * as Yup from 'yup';
import styles from "../../View_Property/ViewPropertyStyles/ViewPropertyStyle.module.scss"
import { Generate_Utility_Invoice, Get_Utility_Type } from '../../View_Property/View_Property_Api';
import Swal from 'sweetalert2';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { CircularProgress } from '@mui/material';

export interface Field {
    utility_type_id: string,
    utility_name: string,
    amount: string,
    sgst: string,
    cgst: string,
    gst_applicable: boolean,
}

export interface FormValues {
    month: string,
    fields: Field[];
}
interface UtilityDetailsModalDataType {
    show: boolean
    handleClose: () => void
    property_sub_unit_uuid: string,
    propertyUUID: string
    getTenantRentPaymentList: (id: string,
        property_sub_unit_uuid: string,
        forceFetch: boolean) => void
    id: string
}
interface utilityTypeDataType {
    cgst: number | null,
    sgst: number | null,
    utility_type_id: number,
    utility_type_key: string,
    utility_type_name: string,
    utility_type_uuid: string
}
const modalsRoot = document.getElementById('root-modals') || document.body

const UtilityDetailsModal = ({
    show,
    handleClose,
    property_sub_unit_uuid,
    propertyUUID,
    getTenantRentPaymentList,
    id
}: UtilityDetailsModalDataType) => {
    const newUtilityRef = useRef<HTMLDivElement>(null)
    const modalHeaderRef = useRef<HTMLDivElement>(null)
    const errorRef = useRef<HTMLDivElement>(null)
    const [utilityType, setUtilityType] = useState<utilityTypeDataType[]>([])
    const [selectedDate, setSelectedDate] = useState<Date | null>(null);
    const [submitBtnLoading, setSubmitBtnLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>("")
    const getUtilityType = async () => {
        try {
            const { data: response } = await Get_Utility_Type()
            if (response.code === '200') {
                setUtilityType(response.data)
            } else {
                Swal.fire({
                    position: 'center',
                    icon: 'error',
                    title: response.msg.charAt(0).toUpperCase() + response.msg.slice(1),
                    text: response.msg_details,
                    allowOutsideClick: true,
                })
            }
        } catch (error: any) {
            Swal.fire({
                position: 'center',
                icon: 'error',
                title: "Error",
                text: error,
            })
        }
    }

    useEffect(() => {
        getUtilityType()
    }, [])
    const validationSchema = Yup.object().shape({
        month: Yup.string().required('Month is required'),
        fields: Yup.array().of(
            Yup.object().shape({
                utility_type_id: Yup.string().required('Utility type is required'),
                utility_name: Yup.string()
                    .min(3, 'Minimum 3 characters')
                    .max(50, 'Maximum 50 characters')
                    .required('Utility name is required'),
                amount: Yup.string().required('Amount is required'),
                gst_applicable: Yup.string().required('Field value is required'),
                sgst: Yup.string().when('gst_applicable', (val, schema) => {
                    if (val === 'true') return Yup.number()
                        .typeError('SGST value must be a number')
                        .positive('SGST value must be a positive number')
                        .integer('SGST value must be an integer')
                        // .min(100000, 'SGST value must be at least 6 digits')
                        // .max(999999, 'SGST value cannot be more than 6 digits')
                        .required('SGST value is required')
                    else return Yup.string().notRequired()
                }),
                cgst: Yup.string().when('gst_applicable', (val, schema) => {
                    if (val === 'true') return Yup.number()
                        .typeError('CGST value must be a number')
                        .positive('CGST value must be a positive number')
                        .integer('CGST value must be an integer')
                        // .min(100000, 'CGST value must be at least 6 digits')
                        // .max(999999, 'CGST value cannot be more than 6 digits')
                        .required('CGST value is required')
                    else return Yup.string().notRequired()
                }),
            })
        ),
    });

    const initialValues: FormValues = {
        month: "",
        fields: [
            {
                utility_type_id: "",
                utility_name: "",
                sgst: "",
                cgst: "",
                amount: "",
                gst_applicable: true,
            }
        ]
    }

    const findDuplicate = () => {
        const updatedObj: any = {}
        for (const obj of formik.values.fields) {
            if (updatedObj.hasOwnProperty(obj.utility_type_id) && Number(obj.utility_type_id) !== 3) {
                return `Error: Remove duplicate utility types`
            }
            else if (updatedObj.hasOwnProperty(obj.utility_type_id) && Number(obj.utility_type_id) === 3 && updatedObj[3].hasOwnProperty(obj.utility_name)) {
                return `Error: Remove duplicate miscellaneous utility types`
            }
            else if (Number(obj.utility_type_id) === 3) {
                updatedObj[obj.utility_type_id] = { [obj.utility_name]: 1, ...updatedObj[3] }
            }
            else {
                updatedObj[obj.utility_type_id] = 1;
            }
        }
        return false
    }
    const generateUtilityInvoice = async (values: FormValues) => {
        const isDuplicate = findDuplicate()
        if (isDuplicate) {
            setError(isDuplicate)
        } else {
            const updatedArray = formik.values.fields.map((item) => {
                if (item.gst_applicable === false) {
                    const { sgst, cgst, utility_type_id, amount, ...rest } = item;
                    return { utility_type_id: Number(utility_type_id), amount: Number(amount), ...rest };
                } else {
                    const { sgst, cgst, utility_type_id, amount, ...rest } = item;
                    return { sgst: Number(sgst), cgst: Number(cgst), utility_type_id: Number(utility_type_id), amount: Number(amount), ...rest }
                }
            })
            setSubmitBtnLoading(true)
            try {
                const { data: res } = await Generate_Utility_Invoice(
                    property_sub_unit_uuid,
                    propertyUUID,
                    formik.values.month,
                    updatedArray
                )
                if (res.code === '200') {
                    getTenantRentPaymentList(
                        id,
                        property_sub_unit_uuid,
                        true)
                    formik.resetForm()
                    handleClose()
                    Swal.fire({
                        position: 'center',
                        icon: 'success',
                        title: res.msg.charAt(0).toUpperCase() + res.msg.slice(1),
                        text: res.msg_details,
                        allowOutsideClick: true,
                    })
                } else {
                    setError(`${res.msg.charAt(0).toUpperCase() + res.msg.slice(1)}: ${res.msg_details}`)
                }
            }
            catch (error: any) {
                setError(`${error}`)
            }
            finally {
                setSubmitBtnLoading(false)
            }
        }
    }
    useEffect(() => {
        if (errorRef && errorRef.current) {
          errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, [error]);
    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit: (values) => generateUtilityInvoice(values),
    })

    const handleAddField = () => {
        if (formik.values.fields.length <= 9) {
            const newField: Field = {
                utility_type_id: "",
                utility_name: "",
                sgst: "",
                cgst: "",
                amount: "",
                gst_applicable: true,
            };

            formik.setValues({
                ...formik.values,
                fields: [...formik.values.fields, newField],
            });
            if (newUtilityRef && newUtilityRef.current) {
                newUtilityRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }
        }
    };
    const handleGSTChange = (value: boolean, index: number) => {
        formik.setFieldValue(`fields[${index}].gst_applicable`, value, true)
    }

    const handleUtilityTypeAndId = (value: string, index: number) => {
        if (value) {
            const selectedOption = utilityType.find(option => option.utility_type_id === parseInt(value, 10));
            if (selectedOption) {
                if (selectedOption.utility_type_id === 3) {
                    formik.setFieldValue(`fields[${index}].utility_name`, "", true)
                    formik.setFieldValue(`fields[${index}].sgst`, "", true)
                    formik.setFieldValue(`fields[${index}].cgst`, "", true)
                } else {
                    formik.setFieldValue(`fields[${index}].utility_name`, selectedOption.utility_type_name, true)
                    formik.setFieldValue(`fields[${index}].sgst`, selectedOption.sgst, true)
                    formik.setFieldValue(`fields[${index}].cgst`, selectedOption.cgst, true)
                }
            }
        } else {
            formik.setFieldValue(`fields[${index}].utility_name`, "", true)
            formik.setFieldValue(`fields[${index}].sgst`, "", true)
            formik.setFieldValue(`fields[${index}].cgst`, "", true)
        }
        formik.setFieldValue(`fields[${index}].utility_type_id`, value, true)
    }
    // function to delete the signer
    const handleDeleteSigner = async (i: number) => {
        const updatedArray = formik.values.fields.filter((_: any, index: number) => index !== i)
        formik.setFieldValue(`fields`, updatedArray, true)
    }
    // function to format the date
    const formatDate = (date: Date) => {
        const formattedDate = date.toLocaleDateString('en-CA', {
            year: 'numeric',
            month: '2-digit',
        })
        return formattedDate
    }
    return createPortal(
        <Modal
            id='kt_modal_create_app'
            tabIndex={-1}
            aria-hidden='true'
            dialogClassName='modal-dialog modal-dialog-centered mw-1000px'
            show={show ? true : false}
            onHide={handleClose}
            backdrop={true}
        >
            {/* Begin: Modal header */}
            <div className='modal-header pt-5 pb-5' ref={modalHeaderRef}>
                {/* Begin: Modal Heading */}
                <h2>Add Utility Details</h2>
                {/* End: Modal Heading */}
                {/* Begin::Close button */}
                <div className='btn btn-sm btn-icon btn-active-color-primary' onClick={handleClose}>
                    <i className='bi bi-x-lg' onClick={handleClose}></i>
                </div>
                {/* end::Close button */}
            </div>
            {/* End: Modal header */}
            {/* Begin: Error msg section */}
            {error && (
                <div ref={errorRef} className='h-25px d-flex justify-content-center align-items-center' style={{ backgroundColor: 'red' }}>
                    <span className='fv-help-block' style={{ color: 'white' }}>
                        {error}
                    </span>
                    <button className='btn px-3 py-0 h-20px btn-secondary ms-2' type='button' onClick={() => setError("")}>Ok</button>
                </div>
            )}
            {/* End: Error msg section */}

            {/* Begin: Modal body */}
            {utilityType.length < 0 ?
                <CircularProgress />
                :
                <div className='modal-body p-0'>
                    <div className='card-body p-9 pt-2 row'>
                        <form onSubmit={formik.handleSubmit}>
                            {/* Render existing fields */}
                            <div className='row ps-5 justify-content-between'>
                                <div className='col-12 col-lg-3'>
                                    <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                        <span className='required'>Month</span>
                                        <i
                                            className='fas fa-exclamation-circle ms-2 fs-7'
                                            data-bs-toggle='tooltip'
                                            title='Enter month and year'
                                        ></i>
                                    </label>
                                    <DatePicker
                                        className='form-control form-control-lg form-control-solid'
                                        autoComplete='off'
                                        selected={selectedDate}
                                        dateFormat="MM/yyyy"
                                        placeholderText='Enter month and year'
                                        showMonthYearPicker
                                        maxDate={new Date()}
                                        onChange={(date: Date) => {
                                            formik.setFieldTouched('month', true, true)
                                            if (date === null) {
                                                setSelectedDate(date)
                                                formik.setFieldValue('month', '')
                                            } else {
                                                setSelectedDate(date)
                                                formik.setFieldValue('month', formatDate(date))
                                            }
                                        }}
                                    />
                                    <div className='h-20px'>
                                        {formik.touched.month &&
                                            formik.errors.month &&
                                            (
                                                <div className='fv-help-block color-red'>
                                                    {
                                                        formik.errors.month
                                                    }
                                                </div>
                                            )}
                                    </div>
                                </div>
                                <div className='col-12 col-lg-2 align-self-end d-flex justify-content-end'>
                                    <button type='button' className={`align-self-end my-3 btn btn-sm mt-3 ${formik.values.fields.length > 9 ? 'btn-secondary' : 'btn-primary'}`} onClick={handleAddField}>
                                        Add Utility
                                    </button>
                                </div>
                            </div>
                            {formik.values.fields.map((field, index) => (
                                <div ref={newUtilityRef} key={index} className='row w-100 border border-primary border-dashed rounded p-2 m-2'>
                                    <div className='d-flex justify-content-between'>
                                        <h3 className='text-primary'>{`Utility ${index + 1}`}</h3>
                                        {/*Begin::Form Group property type */}
                                        {index > 0 && (
                                            <i
                                                className='bi bi-x-circle-fill fs-2 text-primary'
                                                onClick={() => handleDeleteSigner(index)}
                                                title='Delete utility'
                                            ></i>
                                        )}
                                    </div>
                                    <div className='col-12 col-lg-3 px-1 mb-2'>
                                        <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                            <span className='required'>Utility Type</span>
                                            <i
                                                className='fas fa-exclamation-circle ms-2 fs-7'
                                                data-bs-toggle='tooltip'
                                                title='Choose your property type'
                                            ></i>
                                        </label>
                                        <select
                                            className='form-select form-select-solid'
                                            {...formik.getFieldProps(`fields[${index}].utility_type_id`)}
                                            name={`fields[${index}].utility_type_id`}
                                            placeholder='Select utility type'
                                            onChange={(e) => handleUtilityTypeAndId(e.target.value, index)}
                                        >
                                            <option className='bg-color-gray' value="">
                                                Select utility type
                                            </option>
                                            {utilityType.map((option) => (
                                                <option key={option.utility_type_uuid} className='bg-color-gray' value={option.utility_type_id}>
                                                    {option.utility_type_name}
                                                </option>
                                            ))}
                                        </select>
                                        <div className='h-20px'>
                                            {formik.touched.fields &&
                                                formik.touched.fields[index] &&
                                                formik.touched.fields[index].utility_type_id &&
                                                formik.errors.fields &&
                                                formik.errors.fields[index] &&
                                                // @ts-ignore
                                                formik.errors.fields[index].utility_type_id && (
                                                    <div className='fv-help-block color-red'>
                                                        {
                                                            // @ts-ignore
                                                            formik.errors.fields[index].utility_type_id
                                                        }
                                                    </div>
                                                )}
                                        </div>
                                    </div>
                                    {/*End::Form Group property type */}
                                    {/*Begin::Form Group Full Name */}
                                    <div className='col-12 col-lg-3 px-1 mb-2'>
                                        <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                            <span className='required'>Amount</span>
                                            <i
                                                className='fas fa-exclamation-circle ms-2 fs-7'
                                                data-bs-toggle='tooltip'
                                                title='Enter utility amount'
                                            ></i>
                                        </label>
                                        <input
                                            type='text'
                                            className='form-control form-control-lg form-control-solid'
                                            placeholder='Enter utility amount'
                                            {...formik.getFieldProps(`fields[${index}].amount`)}
                                            name={`fields[${index}].amount`}
                                        />
                                        <div className='h-20px'>
                                            <div className='h-20px'>
                                                {formik.touched.fields &&
                                                    formik.touched.fields[index] &&
                                                    formik.touched.fields[index].amount &&
                                                    formik.errors.fields &&
                                                    formik.errors.fields[index] &&
                                                    // @ts-ignore
                                                    formik.errors.fields[index].amount && (
                                                        <div className='fv-help-block color-red'>
                                                            {
                                                                // @ts-ignore
                                                                formik.errors.fields[index].amount
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                    {/*End::Form Group Full Name */}
                                    <div className='col-12 col-lg-3 px-1 mb-2'>
                                        <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                            <span className={`${formik.values.fields[index].gst_applicable === true && 'required'}`}>CGST</span>
                                            <i
                                                className='fas fa-exclamation-circle ms-2 fs-7'
                                                data-bs-toggle='tooltip'
                                                title='Enter CGST'
                                            ></i>
                                        </label>
                                        <input
                                            type='text'
                                            className={`form-control form-control-lg form-control-solid ${formik.values.fields[index].gst_applicable === false && 'opacity-25 text-decoration-line-through'}`}
                                            placeholder='Enter CGST'
                                            {...formik.getFieldProps(`fields[${index}].cgst`)}
                                            name={`fields[${index}].cgst`}
                                            disabled={formik.values.fields[index].gst_applicable === false || formik.values.fields[index].utility_type_id !== '3'}
                                        />
                                        <div className='h-20px'>
                                            <div className='h-20px'>
                                                {formik.touched.fields &&
                                                    formik.touched.fields[index] &&
                                                    formik.touched.fields[index].cgst &&
                                                    formik.errors.fields &&
                                                    formik.errors.fields[index] &&
                                                    // @ts-ignore
                                                    formik.errors.fields[index].cgst && (
                                                        <div className='fv-help-block color-red'>
                                                            {
                                                                // @ts-ignore
                                                                formik.errors.fields[index].cgst
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                    <div className='col-12 col-lg-3 px-1 mb-2'>
                                        <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                            <span className={`${formik.values.fields[index].gst_applicable === true && 'required'}`}>SGST</span>
                                            <i
                                                className='fas fa-exclamation-circle ms-2 fs-7'
                                                data-bs-toggle='tooltip'
                                                title='Enter SGST'
                                            ></i>
                                        </label>
                                        <input
                                            type='text'
                                            className={`form-control form-control-lg form-control-solid ${formik.values.fields[index].gst_applicable === false && 'opacity-25 text-decoration-line-through'}`}
                                            placeholder='Enter SGST'
                                            {...formik.getFieldProps(`fields[${index}].sgst`)}
                                            name={`fields[${index}].sgst`}
                                            disabled={formik.values.fields[index].gst_applicable === false || formik.values.fields[index].utility_type_id !== '3'}
                                        />
                                        <div className='h-20px'>
                                            <div className='h-20px'>
                                                {formik.touched.fields &&
                                                    formik.touched.fields[index] &&
                                                    formik.touched.fields[index].sgst &&
                                                    formik.errors.fields &&
                                                    formik.errors.fields[index] &&
                                                    // @ts-ignore
                                                    formik.errors.fields[index].sgst && (
                                                        <div className='fv-help-block color-red'>
                                                            {
                                                                // @ts-ignore
                                                                formik.errors.fields[index].sgst
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    </div>
                                    {/*Begin::Form Group Full Name */}
                                    {
                                        formik.values.fields[index].utility_type_id === "3" &&
                                        <div className='col-12 col-lg-3 px-1 mb-2'>
                                            <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                                <span className='required'>Utility Name</span>
                                                <i
                                                    className='fas fa-exclamation-circle ms-2 fs-7'
                                                    data-bs-toggle='tooltip'
                                                    title='Enter utility name'
                                                ></i>
                                            </label>
                                            <input
                                                type='text'
                                                className='form-control form-control-lg form-control-solid'
                                                placeholder='Enter utility name'
                                                {...formik.getFieldProps(`fields[${index}].utility_name`)}
                                                name={`fields[${index}].utility_name`}
                                            />
                                            <div className='h-20px'>
                                                {formik.touched.fields &&
                                                    formik.touched.fields[index] &&
                                                    formik.touched.fields[index].utility_name &&
                                                    formik.errors.fields &&
                                                    formik.errors.fields[index] &&
                                                    // @ts-ignore
                                                    formik.errors.fields[index].utility_name && (
                                                        <div className='fv-help-block color-red'>
                                                            {
                                                                // @ts-ignore
                                                                formik.errors.fields[index].utility_name
                                                            }
                                                        </div>
                                                    )}
                                            </div>
                                        </div>
                                    }
                                    {/*End::Form Group Full Name */}
                                    <div className='col-12 col-lg-4 mb-2'>
                                        <div className='d-flex justify-content-between'>
                                            <label className='d-flex align-items-center fs-5 fw-semibold mb-2'>
                                                <span className='required'>GST Applicable</span>
                                                <i
                                                    className='fas fa-exclamation-circle ms-2 fs-7'
                                                    data-bs-toggle='tooltip'
                                                    title='Enter utility amount'
                                                ></i>
                                            </label>
                                            <label className={`${styles.switch}`}>
                                                <input type="checkbox" {...formik.getFieldProps(`fields[${index}].gst_applicable`)}
                                                    name={`fields[${index}].gst_applicable`}
                                                    // @ts-ignore
                                                    defaultChecked={`fields[${index}].gst_applicable`}
                                                    onClick={(e) => handleGSTChange((e.target as HTMLInputElement).checked, index)}
                                                />
                                                <span className={`${styles.slider} ${styles.round}`}></span>
                                            </label>
                                        </div>
                                    </div>
                                </div >
                            ))}
                            <div className='d-flex justify-content-end'>
                                <button type='button' className={`align-self-end btn btn-sm mt-3 ${formik.values.fields.length > 9 ? 'btn-secondary' : 'btn-primary'}`} onClick={handleAddField}>
                                    Add Utility
                                </button></div>
                            <div className='d-flex justify-content-center mb-4'>
                                {/* Begin:: submit button */}
                                <button
                                    type='submit'
                                    className='btn btn-lg btn-primary mt-5'
                                    // data-kt-stepper-action='submit'
                                    disabled={submitBtnLoading}
                                >
                                    {submitBtnLoading ? (
                                        <span className='indicator-progress' style={{ display: 'block' }}>
                                            Processing...
                                            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                                        </span>
                                    ) : (
                                        'Submit'
                                    )}
                                </button>
                                {/* Begin:: submit button */}
                            </div>
                        </form>
                    </div>
                </div>
            }

        </Modal >,
        modalsRoot
    )
}

export default UtilityDetailsModal